import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// @material-ui/icons
import HideIco from '@material-ui/icons/VisibilityOff';
// @material-ui/core components
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import Hidden from '@material-ui/core/Hidden';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { userRanks, userKinds } from 'config';
import WithPermission from '../WithPermission';

/**
 * @description
 * Stateless component to handle user filters
 *
 * @param {Object} props - Component props.
 */
function UserFilter(props) {
  const {
    classes,
    filter,
    appliedFilter,
    permissionsGroups,
    showFilters,
    t,
  } = props;
  return (
    <GridContainer>
      <GridItem xs={12} sm={6} md={5}>
        <SectionDivider label={t('user.filter.users')}>
          <div className={classes.responsiveHorizontalScroll}>
            <Button
              color="info"
              round
              simple={appliedFilter.isActive !== undefined}
              onClick={() => {
                filter({
                  ...appliedFilter,
                  isActive: undefined,
                });
              }}
              id="filterUserAll"
              key="filterUserAll"
              data-cy="filterActiveUserAll"
            >
              {t('user.filter.all')}
            </Button>
            <Button
              color="info"
              round
              simple={appliedFilter.isActive !== true}
              onClick={() => {
                filter({
                  ...appliedFilter,
                  isActive: true,
                });
              }}
              id="filterUserActive"
              data-cy="filterActiveUser"
            >
              {t('user.filter.active')}
            </Button>
            <Button
              color="info"
              round
              simple={appliedFilter.isActive !== false}
              onClick={() => {
                filter({
                  ...appliedFilter,
                  isActive: false,
                });
              }}
              id="filterUserInactive"
              key="filterUserInactive"
              data-cy="filterInactiveUser"
            >
              {t('user.filter.inactive')}
            </Button>
          </div>
        </SectionDivider>
      </GridItem>
      <GridItem xs={12} sm={6} md={7}>
        <SectionDivider label={t('user.filter.type')}>
          <div className={classes.responsiveHorizontalScroll}>
            <WithPermission allowPermissions={['list_user']}>
              <WithPermission allowPermissions={['list_agency']}>
                <WithPermission allowPermissions={['list_user_agency']}>
                  <Button
                    color="info"
                    round
                    simple={appliedFilter.userType.length !== 0}
                    onClick={() => {
                      filter({
                        ...appliedFilter,
                        userType: [],
                        permissionGroup: undefined,
                      });
                    }}
                    id="filterUserTypeAll"
                    key="filterUserTypeAll"
                    data-cy="filterUserTypeAll"
                  >
                    {t('user.filter.all')}
                  </Button>
                </WithPermission>
              </WithPermission>
            </WithPermission>
            <WithPermission allowPermissions={['list_user']}>
              <Button
                color="info"
                round
                simple={appliedFilter.userType !== userKinds.USER}
                onClick={() => {
                  filter({
                    ...appliedFilter,
                    userType: userKinds.USER,
                    permissionGroup: undefined,
                  });
                }}
                id={`filterUserType${userKinds.USER}`}
                key={`filterUserType${userKinds.USER}`}
                data-cy={`filterUserType${userKinds.USER}`}
              >
                {t('user.filter.users')}
              </Button>
            </WithPermission>
            <WithPermission
              allowPermissions={[
                'list_user_agency',
                'list_ownership_useragency',
              ]}
            >
              <Button
                color="info"
                round
                simple={appliedFilter.userType !== userKinds.USER_AGENCY}
                onClick={() => {
                  filter({
                    ...appliedFilter,
                    userType: userKinds.USER_AGENCY,
                    permissionGroup: undefined,
                  });
                }}
                id={`filterUserType${userKinds.USER_AGENCY}`}
                key={`filterUserType${userKinds.USER_AGENCY}`}
                data-cy={`filterUserType${userKinds.USER_AGENCY}`}
              >
                {t('user.filter.users_agency')}
              </Button>
            </WithPermission>
            <WithPermission allowPermissions={['list_agency']}>
              <Button
                color="info"
                round
                simple={appliedFilter.userType !== userKinds.AGENCY}
                onClick={() => {
                  filter({
                    ...appliedFilter,
                    userType: userKinds.AGENCY,
                    permissionGroup: undefined,
                  });
                }}
                id={`filterUserType${userKinds.AGENCY}`}
                key={`filterUserType${userKinds.AGENCY}`}
                data-cy={`filterUserType${userKinds.AGENCY}`}
              >
                {t('user.filter.agency')}
              </Button>
            </WithPermission>
          </div>
        </SectionDivider>
      </GridItem>
      <GridItem xs={12} sm={6} md={7}>
        <SectionDivider label={t('user.filter.group')}>
          <div className={classes.responsiveHorizontalScroll}>
            <Button
              color="info"
              round
              simple={appliedFilter.rank.length === 1}
              onClick={() => {
                filter({
                  ...appliedFilter,
                  rank: [],
                  permissionGroup: undefined,
                });
              }}
              id="filterUserRankAll"
              key="filterUserRankAll"
              data-cy="filterUserRankAll"
            >
              {t('user.filter.all')}
            </Button>
            <WithPermission allowPermissions={[]}>
              <Button
                color="info"
                round
                simple={
                  appliedFilter.rank.length !== 1 ||
                  !appliedFilter.rank.includes(userRanks.ADMIN)
                }
                onClick={() => {
                  filter({
                    ...appliedFilter,
                    rank: [userRanks.ADMIN],
                    permissionGroup: undefined,
                  });
                }}
                id={`filterUserRank${userRanks.ADMIN}`}
                key={`filterUserRank${userRanks.ADMIN}`}
                data-cy={`filterUserRank${userRanks.ADMIN}`}
              >
                Admin
              </Button>
            </WithPermission>
            <Button
              color="info"
              round
              simple={
                appliedFilter.rank.length !== 1 ||
                !appliedFilter.rank.filter(rank => rank === userRanks.USER)
                  .length
              }
              onClick={() => {
                filter({
                  ...appliedFilter,
                  rank: [userRanks.USER],
                  permissionGroup: undefined,
                });
              }}
              id="filterUserRankUser"
              key="filterUserRankUser"
              data-cy="filterUserRankUser"
            >
              Usuario
            </Button>
            <Button
              color="info"
              round
              simple={
                appliedFilter.rank.length !== 1 ||
                !appliedFilter.rank.filter(rank => rank === userRanks.STAFF)
                  .length
              }
              onClick={() => {
                filter({
                  ...appliedFilter,
                  rank: [userRanks.STAFF],
                  permissionGroup: undefined,
                });
              }}
              id="filterUserRankStaff"
              key="filterUserRankStaff"
              data-cy="filterUserRankStaff"
            >
              Staff
            </Button>
          </div>
        </SectionDivider>
      </GridItem>
      <GridItem xs={12}>
        <div
          className={`${classes.loteActionsAnimation}
                    ${
                      appliedFilter.rank.length === 1 &&
                      appliedFilter.rank.filter(
                        rank => rank === userRanks.STAFF,
                      ).length
                        ? classes.showFilters
                        : classes.hideFilters
                    }`}
        >
          <GridContainer>
            <GridItem xs={12}>
              <WithPermission allowPermissions={['list_group']}>
                <SectionDivider label={t('user.filter.group')}>
                  <div className={classes.responsiveHorizontalScroll}>
                    <Button
                      color="info"
                      round
                      simple={appliedFilter.permissionGroup !== undefined}
                      onClick={() => {
                        filter({
                          ...appliedFilter,
                          permissionGroup: undefined,
                        });
                      }}
                      id="filterPermissionGroupAll"
                      key="filterPermissionGroupAll"
                      data-cy="tienen el permiso seleccionado"
                    >
                      {t('user.filter.all')}
                    </Button>
                    {permissionsGroups &&
                      permissionsGroups.edges.map(edge => (
                        <Button
                          color="info"
                          round
                          simple={
                            appliedFilter.permissionGroup !== edge.node.id
                          }
                          onClick={() => {
                            filter({
                              ...appliedFilter,
                              permissionGroup: edge.node.id,
                            });
                          }}
                          id={`filterPermissionGroup${edge.node.id}`}
                          key={`filterPermissionGroup${edge.node.id}`}
                          data-cy={`filterPermissionGroup${edge.node.id}`}
                        >
                          {edge.node.name}
                        </Button>
                      ))}
                  </div>
                </SectionDivider>
              </WithPermission>
            </GridItem>
          </GridContainer>
        </div>
      </GridItem>

      <GridItem xs={12}>
        <GridContainer justify="space-between">
          <Button
            color="primary"
            simple
            className={classes.buttons}
            onClick={showFilters}
            id="hideFilters"
            key="hideFilters"
          >
            <HideIco />
            {t('user.filter.hide')}
            <Hidden xsDown implementation="css">
              <div style={{ marginLeft: 4 }}>Filtros</div>
            </Hidden>
          </Button>
          <Button
            color="danger"
            simple
            disabled={
              appliedFilter.rank.length === 0 &&
              appliedFilter.isActive === undefined &&
              appliedFilter.permissionGroup === undefined &&
              appliedFilter.userType.length === 0
            }
            onClick={() => {
              filter({
                rank: [],
                isActive: undefined,
                permissionGroup: undefined,
                userType: [],
              });
            }}
            id="resetFilters"
            key="resetFilters"
            data-cy="resetFilters"
          >
            {t('user.filter.clean')}
            <Hidden xsDown implementation="css">
              <div style={{ marginLeft: 4 }}>
                {t('user.filter.clean_break')}
              </div>
            </Hidden>
          </Button>
        </GridContainer>
      </GridItem>
    </GridContainer>
  );
}

/**
 * Filter PropTypes
 * @param classes - prop styles received from withStyles
 * @param appliedFilter - prop to know applied filters
 * @param permissionsGroups - prop permissions groups received from container
 * @param t - prop from HOC with string translations
 * @callback filter - callback called when user clicks filter button
 * @callback showFilters - callback called when user clicks collapse filters
 * button
 */

UserFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  permissionsGroups: PropTypes.shape({}),
  filter: PropTypes.func.isRequired,
  showFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

UserFilter.defaultProps = {
  permissionsGroups: null,
};

export default withTranslation()(withStyles(extendedFormsStyle)(UserFilter));
